<template>
  <el-dialog
    ref="dialog"
    v-loading="pending"
    :append-to-body="appendToBody"
    :before-close="beforeClose"
    class="form-dialog"
    :custom-class="customClass"
    el-loading-background="fixed-white"
    :el-loading-opacity=".8"
    :el-loading-size="48"
    :el-loading-text="loadingText"
    :heightSize="heightSize"
    :title="title"
    :visible="visible"
    :widthSize="widthSize"
    :close-on-click-modal="closeOnClickModal"
    :data-loaded="dataLoaded"
    @close="onCancel"
    @keyup.enter.stop="handleSubmit"
  >
    <template #body-header>
      <slot name="body-header" />
    </template>
    <form
      v-if="!isErrorCriticalDataRetrieveError"
      :id="formId"
      class="form"
      :class="className"
      @submit.prevent="handleSubmit()"
    >
      <div class="content">
        <slot :valid="valid" />
      </div>
    </form>
    <error-info
      v-else
      :error-message="loadingErrors.message"
      :error-details="loadingErrors.details"
      display-reload-button
      @reloadData="reloadData"
    />
    <confirm
      ref="confirmClose"
      :showClose="true"
    >
      <slot name="beforeCloseContent">
        {{ $t('Are you sure that you want to close this window? All your changes will be discarded.') }}
      </slot>
    </confirm>
    <template #body-footer>
      <slot name="body-footer">
        <div
          v-if="allErrors.length > 0 && !hideErrors"
          class="errors is-error"
        >
          <el-alert
            v-for="error in allErrors"
            :key="error"
            :border="{top: true, bottom: false}"
            hide-close
            show-icon
            type="error"
            data-testid="form-dialog_error"
          >
            <template #description>
              {{ error }}
            </template>
          </el-alert>
        </div>
      </slot>
    </template>
    <template #footer-aside>
      <slot name="footer-aside" />
    </template>
    <template #footer>
      <span>
        <slot name="footer">
          <el-button
            data-testid="form-dialog_cancel"
            type="secondary"
            @click="onCancel"
          >
            {{ cancelButtonText || $t('Cancel') }}
          </el-button>
          <el-button
            :data-operationid="operationId"
            data-testid="form-dialog_submit"
            :disabled="!canSubmit || applyButtonDisabled || (disableApplyButtonOnPristine && pristine)"
            :form="formId"
            native-type="submit"
            :type="applyButtonType"
          >
            {{ applyButtonText || $t('Apply') }}
          </el-button>
        </slot>
      </span>
    </template>
  </el-dialog>
</template>

<script>
  import { Form } from '@uikit/ui-kit';
  import { trackEvent } from '@/common/telemetry';
  import FormMixin from '@/common/mixins/form.mixin';
  import Confirm from '@/common/shared/Confirm.vue';
  import ErrorInfo from '@/common/shared/ErrorInfo.vue';
  import CriticalDataRetrieveError from '@/common/utils/errors/CriticalDataRetrieveError';
  import { debounce } from 'lodash-es';

  export default {
    name: 'FormDialog',
    components: { ErrorInfo, Confirm },
    mixins: [Form, FormMixin],
    props: {
      visible: Boolean,
      // set true to show spinner in dialog footer actions
      pending: Boolean,
      // set true to show spinner in dialog body
      loading: Boolean,
      loadingText: String,
      hideErrors: Boolean,
      hideValidationErrors: {
        type: Boolean,
        default: false
      },
      title: String,
      cancelButtonText: String,
      applyButtonText: String,
      applyButtonDisabled: Boolean,
      operationId: String,
      customClass: {
        type: String,
        default: 'form-dialog'
      },
      confirmOnClose: {
        type: Boolean,
        default: false
      },
      applyButtonType: {
        type: String,
        default: 'primary'
      },
      appendToBody: {
        type: Boolean,
        default: true
      },
      beforeValidate: Function,
      submit: Function,
      heightSize: {
        type: String,
        default: 'auto',
        validator: value => !value || ['auto', 'fixed-auto', 'fixed-medium'].indexOf(value) !== -1
      },
      widthSize: {
        type: String,
        default: 'small'
      },
      errors: {
        type: [Array, Object],
        default() {
          return [];
        }
      },
      loadingErrors: null,
      displayValidationErrors: {
        type: [Array],
        default() {
          return [];
        }
      },
      disableApplyButtonOnPristine: {
        type: Boolean,
        default: false
      },
      closeOnClickModal: {
        type: Boolean,
        default: true
      },
      dataLoaded: Boolean
    },
    data() {
      return {
        dialogBody: null,
        loadingElement: null
      };
    },
    mounted() {
      this.trackState('init');
    },
    computed: {
      formId() {
        // eslint-disable-next-line no-underscore-dangle
        return `formId-${this._uid}`;
      },
      loadingData() {
        return {
          visible: this.loading,
          target: this.dialogBody
        };
      },
      isErrorCriticalDataRetrieveError() {
        return this.loadingErrors instanceof CriticalDataRetrieveError;
      },
      invalidFields() {
        return this.fields
          .filter(field => field.errorMessage)
          .map(field => ({ prop: field.prop, errorMessage: field.errorMessage }));
      },
      /**
       * @example
       * displayValidationErrors: ['object.key']
       * invalidFields: [{ prop: 'object[0].key', errorMessage: 'error' }]
       * return ['error']
       *
       * displayValidationErrors: ['object']
       * invalidFields: [{ prop: 'object[0].key', errorMessage: 'error' }]
       * return ['error']
       */
      validationErrors() {
        return this.invalidFields.reduce((acc, fieldError) => {
          const invalidFieldProp = fieldError.prop.replace(/\[\d\]/g, '');
          const isMatch = this.displayValidationErrors.some(prop => invalidFieldProp.startsWith(prop));
          const isUnique = !acc.includes(fieldError.errorMessage);
          if (isMatch && isUnique) {
            acc.push(fieldError.errorMessage);
          }
          return acc;
        }, []);
      },
      allErrors() {
        if (this.hideValidationErrors) {
          return this.errors;
        }

        if (Array.isArray(this.errors)) {
          return this.errors.concat(this.validationErrors);
        }

        return this.validationErrors;
      },
      canSubmit() {
        return !this.invalidFields.length;
      }
    },
    watch: {
      loadingData: {
        immediate: true,
        deep: true,
        handler(val) {
          if (val.visible && val.target) {
            this.trackState('loading_data_start');
            this.loadingElement = this.$loading({
              background: 'fixed-white',
              target: val.target,
              opacity: '0.8',
              size: '32',
              lock: true
            });
          } else if (this.loadingElement) {
            this.trackState('loading_data_finish');
            this.loadingElement.close();
          }
        }
      },
      visible: {
        immediate: true,
        async handler(val) {
          await this.$nextTick();
          if (val) {
            this.dialogBody = this.$el.querySelector('.el-dialog__body');
            this.$emit('open');
          }
        }
      },
      invalidFields: debounce(function (newValue) {
        this.$emit('validate', newValue);
      }, 350)
    },
    methods: {
      onCancel() {
        this.trackState('close');
        this.$emit('close');
      },
      handleSubmit() {
        if (this.pending) {
          this.trackState('submit_while_pending');
          return Promise.resolve();
        }
        this.clearValidate();
        this.trackState('submit');
        return ((typeof this.beforeValidate === 'function')
          ? this.beforeValidate(this.model)
          : Promise.resolve())
          .then(() => this.validate())
          .then(() => this.trackState('submit_client_validation_success'))
          .then(() => typeof this.submit === 'function' ? this.submit(this.model) : Promise.resolve(this.model))
          .then((res) => {
            this.trackState('submit_success');
            return res;
          })
          .catch(() => {
            this.trackState('submit_error', { errors: this.validationErrors });
            this.$emit('error');
            this.scrollToFirstError();
          });
      },
      async scrollToFirstError() {
        await this.$nextTick();
        const el = this.$el.querySelector('.is-error');
        if (el) {
          el.scrollIntoView();
        }
      },
      beforeClose(done) {
        if (this.confirmOnClose && !this.pristine) {
          this.trackState('confirm_close_show');
          this.$refs.confirmClose.create({
            buttonCancel: this.$t('Cancel'),
            buttonConfirm: this.$t('Close'),
            title: this.$t('Close'),
            action: () => {
              this.trackState('confirm_close_finish');
              done();
            },
            type: 'primary'
          });
        } else {
          done();
        }
      },
      reloadData() {
        this.trackState('reload_data');
        this.$emit('reloadData');
      },
      trackState(state, args = {}) {
        trackEvent('form_dialog_funnel', {
          title: this.title,
          route: this.$route?.name,
          formId: this.formId,
          operationId: this.operationId,
          state,
          ...args
        });
      }
    },
  };
</script>

<style scoped lang="scss">
  .form {
    display: flex;
    width: 100%;
    min-height: 100%;
    flex-grow: 1;
  }

  .content {
    flex: 1 1 auto;
    max-width: 100%;
  }

  ::v-deep .form-error {
    word-break: break-word;
  }

</style>
